import React, { useState, useEffect, useMemo } from 'react';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/context/ToastContext';
import { clientApi } from '@/lib/api';
import { Table, TableColumn, TableAction } from '@/components/Table';
import { Icon } from '@/components/Icon';
import { Button } from '@/components/Button';
import { Badge } from '@/components/Badge';
import { PageHeader } from '@/components/PageHeader';
import { ListPanel } from '@/components/ListPanel';
import { ModalDelete } from '@/components/ModalDelete';
import { Select, SelectOption } from '@/components/Select';
import { ClientItem, CountryItem, ProvinceItem } from '@/pages/Clients/types';
import { ClientType } from '@/pages/Tenders/types';
import { ClientModal } from '@/pages/Clients/ClientModal';
import { ClientDetailModal } from '@/pages/Clients/ClientDetailModal';
import { ListSettings } from '@/components/ListSettings';
import { SavedViews } from '@/components/SavedViews';
import { listPreferencesSchema, listSortSchema, listViewSchema, type ListPreferences } from '@/components/listPreferences';
import { useStoredPreference } from '@/hooks/useStoredPreference';
import { useServerList, useDebouncedSearch } from '@/hooks/useServerList';
import { useRecordDialog } from '@/hooks/useRecordDialog';

export const ClientsList: React.FC = () => {
    const { t } = useTranslation('global');
    const { showSuccess, showError } = useToast();

    const [clientTypes, setClientTypes] = useState<ClientType[]>([]);
    const [countries, setCountries] = useState<CountryItem[]>([]);
    const [provinces, setProvinces] = useState<ProvinceItem[]>([]);
    const [selectedTypeId, setSelectedTypeId] = useStoredPreference<number | null>('vigepra_clients_type', z.number().nullable(), null, true);
    const [search, setSearch] = useStoredPreference('vigepra_clients_search', z.string().max(500), '', true);
    const [sort, setSort] = useStoredPreference<z.infer<typeof listSortSchema>>('vigepra_clients_sort', listSortSchema, { key: 'name', order: 'asc' });
    const delayedSearch = useDebouncedSearch(search);
    const listing = useServerList<ClientItem>('clients', '/clients', { search: delayedSearch, client_type_id: selectedTypeId || undefined, sort_by: sort.key, sort_order: sort.order });
    const clients = listing.items;
    const isLoading = listing.loading;
    const [filtersOpen, setFiltersOpen] = useState<boolean>(false);
    const [preferences, setPreferences] = useStoredPreference<ListPreferences>(
        'vigepra_clients_table',
        listPreferencesSchema,
        { density: 'comfortable', hidden: [] },
    );

    const record = useRecordDialog<ClientItem>('clients');
    const detailClient = record.item;
    const isDetailModalOpen = Boolean(detailClient);
    const setDetailClient = record.select;

    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [editingClient, setEditingClient] = useState<ClientItem | null>(null);

    const [clientToDelete, setClientToDelete] = useState<ClientItem | null>(null);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    const fetchData = async (): Promise<void> => {
        try {
            const [typesRes, countriesRes, provincesRes] = await Promise.all([
                clientApi.types(),
                clientApi.countries(),
                clientApi.provinces(),
            ]);

            if(typesRes?.data && Array.isArray(typesRes.data)){
                setClientTypes(typesRes.data);
            }

            if(countriesRes?.data && Array.isArray(countriesRes.data)){
                setCountries(countriesRes.data);
            }

            if(provincesRes?.data && Array.isArray(provincesRes.data)){
                setProvinces(provincesRes.data);
            }
        }
        catch(err: unknown){
            showError(err instanceof Error ? err.message : t('common.error'));
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleOpenDetailModal = (client: ClientItem): void => {
        setDetailClient(client);
    };

    const handleCloseDetailModal = (): void => {
        setDetailClient(null);
    };

    const handleOpenCreateModal = (): void => {
        setEditingClient(null);
        setIsModalOpen(true);
    };

    const handleOpenEditModal = (client: ClientItem): void => {
        setEditingClient(client);
        setIsModalOpen(true);
    };

    const handleCloseModal = (): void => {
        setIsModalOpen(false);
        setEditingClient(null);
    };

    const handleClientSaved = (savedClient: ClientItem): void => {
        listing.reload();

        if(detailClient?.id === savedClient.id){
            setDetailClient(savedClient);
        }
    };

    const handleOpenDeleteModal = (client: ClientItem): void => {
        setClientToDelete(client);
    };

    const handleCloseDeleteModal = (): void => {
        setClientToDelete(null);
    };

    const handleConfirmDelete = async (): Promise<void> => {
        if(!clientToDelete){
            return;
        }

        setIsDeleting(true);
        try {
            await clientApi.delete(clientToDelete.id);
            listing.reload();
            showSuccess(t('clients.deleted_success'));
            handleCloseDeleteModal();
        }
        catch(err: unknown){
            showError(err instanceof Error ? err.message : t('common.error'));
        }
        finally {
            setIsDeleting(false);
        }
    };

    const filteredClients = clients;

    const typeFilterOptions: SelectOption<number>[] = useMemo(() => {
        return [
            { value: 0, label: t('clients.all_types') },
            ...clientTypes.map((ct) => ({
                value: ct.id,
                label: ct.name,
            })),
        ];
    }, [clientTypes, t]);

    const columns: TableColumn<ClientItem>[] = useMemo(() => [
        {
            key: 'name',
            label: t('clients.name'),
            sortable: true,
            render: (row: ClientItem) => (
                <div className="font-semibold text-slate-800 dark:text-white">
                    {row.name}
                </div>
            ),
        },
        {
            key: 'client_type',
            label: t('clients.client_type'),
            sortable: true,
            render: (row: ClientItem) => {
                const clientType = row.client_type;
                const clientTypeName = clientType?.name;
                if(!clientTypeName){
                    return '';
                }
                const isPublic = clientTypeName.toLowerCase().includes('públic') || clientTypeName.toLowerCase().includes('public');
                const isCivil = clientTypeName.toLowerCase().includes('civil');
                const defaultFallback = isPublic ? 'success' : (isCivil ? 'neutral' : 'warning');
                const defaultIcon = isPublic ? 'landmark' : (isCivil ? 'hardHat' : 'house');

                const color = clientType?.color || defaultFallback;
                const icon = clientType?.icon || defaultIcon;

                return (
                    <Badge
                        color={color}
                        icon={icon}
                        value={clientTypeName}
                    />
                );
            },
        },
        {
            key: 'tax_id',
            label: t('clients.tax_id'),
            sortable: true
        },
        {
            key: 'phone',
            label: t('clients.phone'),
            render: (row: ClientItem) => {
                if(row.phone){
                    return (
                        <a
                            href={`tel:${row.phone}`}
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                            className="text-slate-600 dark:text-zinc-300 hover:text-primary transition-colors flex items-center gap-1.5"
                        >
                            <Icon value="phone" size={13} className="text-slate-400" />
                            <span>{row.phone}</span>
                        </a>
                    );
                }                
            },
        },
        {
            key: 'email',
            label: t('clients.email'),
            render: (row: ClientItem) => {
                if(row.email){
                    return (
                        <a
                            href={`mailto:${row.email}`}
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                            className="text-slate-600 dark:text-zinc-300 hover:text-primary transition-colors inline-block"
                        >                            
                            <span className="truncate max-w-[180px]">{row.email}</span>
                        </a>
                    );
                }
            },
        },
        {
            key: 'city',
            label: t('clients.city'),
            sortable: true
        },
        {
            key: 'is_active',
            label: t('clients.col_active'),
            sortable: true,
            render: (row: ClientItem) => (
                <Badge
                    color={row.is_active ? 'success' : 'neutral'}
                    value={row.is_active ? t('common.yes') : t('common.no')}
                />
            ),
        },
    ], [t]);

    const actions: TableAction<ClientItem>[] = useMemo(() => [
        {
            label: t('clients.view_details'),
            icon: <Icon value="eye" size={15} />,
            onClick: (row: ClientItem) => handleOpenDetailModal(row),
        },
        {
            label: t('common.edit'),
            icon: <Icon value="pencil" size={15} />,
            onClick: (row: ClientItem) => handleOpenEditModal(row),
        },
        {
            label: t('common.delete'),
            icon: <Icon value="trash" size={15} />,
            variant: 'delete',
            onClick: (row: ClientItem) => handleOpenDeleteModal(row),
        },
    ], [t]);

    const visibleColumns = columns.filter(column => !preferences.hidden.includes(column.key));
    const selectedType = clientTypes.find(type => Number(type.id) === selectedTypeId);
    const clientViewState = { search, filters: { type: String(selectedTypeId || '') }, preferences, sort, pageSize: listing.pagination.pageSize, context: '' };

    return (
        <div className="w-full space-y-3 animate-fade-in">
            <PageHeader
                compact
                className="!mb-0"
                title={t('clients.title')}
                //icon="users"
                action={{
                    label: t('clients.new_client'),
                    icon: 'plus',
                    onClick: handleOpenCreateModal
                }}
            />

            <Table<ClientItem>
                type="simple"
                appearance="workspace"
                density={preferences.density}
                columns={visibleColumns}
                data={filteredClients}
                searchValue={search}
                onSearchChange={setSearch}
                sort={sort}
                onSortChange={setSort}
                manualSorting
                pagination={listing.pagination}
                error={listing.error || record.error}
                onRetry={() => { listing.reload(); record.reload(); }}
                actions={actions}
                actionsPosition="right"
                actionMenu={true}
                paginated={true}
                defaultPageSize={20}
                searchable={true}
                filterbox={false}
                searchPlaceholder={t('clients.search_placeholder')}
                searchKeys={['name', 'tax_id', 'phone', 'email', 'city']}
                emptyText={t('clients.no_clients')}
                isLoading={isLoading}
                onRowClick={handleOpenDetailModal}
                filterOpen={filtersOpen}
                onFilterToggle={() => setFiltersOpen(previous => !previous)}
                filterLabel={t('providers.ui.filters')}
                filterElement={
                    <div className="flex flex-wrap items-center gap-2">
                        <SavedViews
                            storageKey="vigepra_client_views"
                            schema={listViewSchema}
                            defaultValue={{ search: '', filters: { type: '' }, preferences: { density: 'comfortable', hidden: [] }, sort: { key: 'name', order: 'asc' }, pageSize: 20, context: '' }}
                            configuration={<ListSettings inline columns={columns} value={preferences} onChange={setPreferences} sort={sort} onSortChange={setSort} />}
                            value={clientViewState}
                            onApply={view => {
                                setSelectedTypeId(Number(view.filters.type) || null);
                                setSearch(view.search);
                                setPreferences(view.preferences);
                                setSort(view.sort);
                                listing.pagination.onPageSize(view.pageSize);
                            }}
                        />
                    </div>
                }
                filterFooter={
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                        {selectedType && (
                            <Button variant="outline" size="xs" icon="x" onClick={() => setSelectedTypeId(null)}>
                                {selectedType.name}
                            </Button>
                        )}
                        {selectedType && <Button variant="ghost" size="xs" onClick={() => setSelectedTypeId(null)}>{t('workspace.list.clear')}</Button>}
                    </div>
                }
                defaultSortKey='name'
                filterPanel={<ListPanel isOpen={filtersOpen} onClose={() => setFiltersOpen(false)} title={t('providers.ui.filters')}
                    footer={<Button size="sm" variant="ghost" onClick={() => setSelectedTypeId(null)}>{t('workspace.list.clear')}</Button>}>
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                        <Select<number> label={t('clients.client_type')} value={selectedTypeId ?? 0}
                            onChange={(val: number | string) => setSelectedTypeId(val ? Number(val) : null)} options={typeFilterOptions} placeholder={t('clients.all_types')} />
                    </div>
                </ListPanel>}
            />

            <ClientDetailModal
                client={detailClient}
                isOpen={isDetailModalOpen}
                onClose={handleCloseDetailModal}
                onEdit={handleOpenEditModal}
                onDelete={handleOpenDeleteModal}
                clientTypes={clientTypes}
                countries={countries}
                provinces={provinces}
            />

            <ClientModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSaved={handleClientSaved}
                client={editingClient}
                clientTypes={clientTypes}
                existingClients={clients}
            />

            <ModalDelete
                isOpen={Boolean(clientToDelete)}
                onClose={handleCloseDeleteModal}
                title={t('clients.delete_confirm_title')}
                description={t('clients.delete_confirm_desc')}
                itemName={clientToDelete?.name || ''}
                onConfirm={handleConfirmDelete}
                isDeleting={isDeleting}
            />
        </div>
    );
};
